/**
 * Channel settings (UI review High #3): About · Access · Notifications · Posting · Translation · Sidebar colour ·
 * Members · Leave & archive. Renaming, archiving and channel defaults need chat.channel.manage (the API checks);
 * everyone can see the values and change their own notifications. Members' own choices (auto-translate, thread view)
 * always win over defaults.
 *
 * t449u: edits on every tab are kept until Save, which saves them all and closes the dialog; a failure shows here
 * and the dialog stays open. Cancel, Esc, the ✕ and a click outside close without saving. Members, extra roles,
 * the channel form and Leave & archive are actions, so they happen straight away.
 *
 * t722u: Access starts with the Private switch. Changing it asks first (Save / Cancel, one line saying what happens)
 * and then saves straight away, like Archive. People who can manage the channel only; a channel that follows its
 * project shows it turned off with a note to change it in the project's Settings.
 *
 * t449u: workspace Owners also see Delete channel there. Only an empty channel that isn't a default channel or linked
 * to something can go; otherwise the option is shown disabled with the reason. Typing the name confirms.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Archive, Lock, LogOut, Trash2 } from 'lucide-react';
import { channelPrivacyEffect, channelPrivacyProjectNote, channelPrivacyTitle, CHANNEL_PRIVATE_LABEL, type ChannelAccessUnits, type ChannelRules, type ChannelSettings, type Conversation, type ForwardingPolicy } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { useAiStatus } from '@/lib/translate';
import { BuildingSelect } from '@/features/buildings/common';
import { SidebarColourSettings, useSidebarColour, type SidebarColourDraft } from './SidebarColours';
import { ChannelAccessPicker } from './ChannelAccess';
import { ChannelPermissionsSection, rulesForSave, sameRules } from './ChannelPermissions';
import { FormJourneyLinkFields, useFormJourneyLink } from '@/features/fleet/FormJourneyLink';
import { GrantsPanel } from '@/features/admin/permissions/GrantsPanel';
import { useCan } from '@/lib/permissions';
import { Button, DialogFooter, Field, Input, Modal, Select, Tabs, Textarea, Toggle, ask } from '@/components/ui';

type Tab = 'about' | 'access' | 'notifications' | 'posting' | 'translation' | 'colour' | 'members' | 'danger';
type Notify = 'all' | 'mentions' | 'none';
type PollPolicy = 'members' | 'moderators';

const sameAccess = (a: ChannelAccessUnits, b: ChannelAccessUnits) =>
  JSON.stringify([[...a.team_ids].sort(), [...a.location_ids].sort()]) === JSON.stringify([[...b.team_ids].sort(), [...b.location_ids].sort()]);

export function ChannelSettingsModal({ conv, initialTab = 'about', moderator, canAssignForm, onClose, onOpenForm, onOpenMembers, onLeave }: {
  conv: Conversation; initialTab?: Tab; moderator: boolean; canAssignForm: boolean; onClose: () => void;
  onOpenForm: () => void; onOpenMembers: () => void; onLeave: () => void;
}) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const ai = useAiStatus();
  const canManage = Boolean(conv.can_manage);
  const canGrant = useCan('admin.roles.manage');
  const colour = useSidebarColour('conversation', conv.id);
  const access = useQuery({ queryKey: ['channel-access', conv.id], queryFn: () => api.channelAccess(conv.id) });
  const [tab, setTab] = useState<Tab>(initialTab);
  const nav = useNavigate();
  // Only workspace Owners are offered Delete channel; the API says whether this one can go and why not.
  const deletion = useQuery({ queryKey: ['channel-delete-check', conv.id], queryFn: () => api.channelDeleteCheck(conv.id), enabled: conv.kind === 'channel', retry: false });
  const owner = Boolean(deletion.data?.owner);
  // t518u: a channel form can create a journey from each submission (shown when fleet is on and there's a form).
  const formInfo = useQuery({ queryKey: ['channel-form', conv.id, conv.form_template_id ?? null], queryFn: () => api.channelForm(conv.id), enabled: Boolean(conv.form_template_id) });
  const formFields = formInfo.data?.template?.fields ?? null;
  const journeyLink = useFormJourneyLink(ws, conv.id, conv.form_template_id ? formFields : null);

  // Saved values, and the pending edits for each tab (kept when switching tabs).
  const savedAbout = { title: conv.title ?? conv.slug ?? '', description: conv.description ?? '', topic: conv.topic ?? '' };
  const [about, setAbout] = useState(savedAbout);
  const [building, setBuilding] = useState<string | null>(conv.location_id ?? null);
  const [settings, setSettings] = useState<ChannelSettings>({});
  const [notify, setNotify] = useState<Notify | null>(null);
  const [poll, setPoll] = useState<PollPolicy | null>(null);
  const [accessDraft, setAccessDraft] = useState<ChannelAccessUnits | null>(null);
  const [colourDraft, setColourDraft] = useState<SidebarColourDraft>({});
  const [permDraft, setPermDraft] = useState<ChannelRules | null>(null);
  const isChannel = conv.kind === 'channel';
  const perms = useQuery({ queryKey: ['channel-permissions', conv.id], queryFn: () => api.channelPermissions(conv.id), enabled: isChannel });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (access.data && !accessDraft) setAccessDraft(access.data); }, [access.data, accessDraft]);

  const s: ChannelSettings = { ...(conv.settings ?? {}), ...settings };
  const setS = (patch: ChannelSettings) => setSettings(p => ({ ...p, ...patch }));
  const ro = !canManage;

  const aboutDirty = canManage && (about.title !== savedAbout.title || about.description !== savedAbout.description || about.topic !== savedAbout.topic);
  const settingsDirty = canManage && Object.entries(settings).some(([k, v]) => (conv.settings ?? {})[k as keyof ChannelSettings] !== v);
  const buildingDirty = canManage && building !== (conv.location_id ?? null);
  const notifyDirty = notify !== null && notify !== conv.notify_level;
  const pollDirty = moderator && poll !== null && poll !== (conv.poll_policy ?? 'members');
  const accessDirty = canManage && Boolean(accessDraft && access.data && !sameAccess(accessDraft, access.data));
  const mineDirty = 'mine' in colourDraft && colourDraft.mine !== colour.mine;
  const defDirty = colour.canManage && 'def' in colourDraft && colourDraft.def !== colour.workspaceDefault;
  const permDirty = canManage && isChannel && permDraft !== null && Boolean(perms.data) && !sameRules(permDraft, perms.data!.rules);
  const dirty = aboutDirty || settingsDirty || buildingDirty || notifyDirty || pollDirty || accessDirty || mineDirty || defDirty || permDirty || journeyLink.dirty;
  const invalid = (aboutDirty && !about.title.trim()) || Boolean(journeyLink.problem);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: qk.conversation(conv.id) });
    void queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
  };

  /** Saves every changed tab together. All are attempted; if any fail, the dialog stays open and says which. */
  const saveAll = async () => {
    const jobs: [string, () => Promise<unknown>][] = [];
    if (aboutDirty || settingsDirty) {
      jobs.push([aboutDirty ? 'About' : 'Posting and translation', () => api.updateConversation(conv.id, {
        ...(aboutDirty ? { title: about.title.trim(), description: about.description, topic: about.topic } : {}),
        ...(settingsDirty ? { settings } : {}),
      })]);
    }
    if (buildingDirty) jobs.push(['Building', async () => { await api.setConversationBuilding(conv.id, building); void queryClient.invalidateQueries({ queryKey: ['buildings'] }); }]);
    if (accessDirty && accessDraft) jobs.push(['Access', async () => {
      await api.setChannelAccess(conv.id, accessDraft);
      void queryClient.invalidateQueries({ queryKey: ['channel-access', conv.id] });
      void queryClient.invalidateQueries({ queryKey: ['conversation-readers', conv.id] });
    }]);
    if (notifyDirty && notify) jobs.push(['Notifications', () => api.updateMembership(conv.id, { notify_level: notify })]);
    if (pollDirty && poll) jobs.push(['Polls and surveys', () => api.setPollPolicy(conv.id, poll)]);
    if (permDirty && permDraft) jobs.push(['Permissions', async () => {
      await api.setChannelPermissions(conv.id, rulesForSave(permDraft));
      void queryClient.invalidateQueries({ queryKey: ['channel-permissions', conv.id] });
    }]);
    if (journeyLink.dirty) jobs.push(['Journeys', journeyLink.save]);
    if (mineDirty) jobs.push(['Your colour', () => colour.putMine(colourDraft.mine ?? null)]);
    if (defDirty) jobs.push(['Workspace colour', () => colour.putDefault(colourDraft.def ?? null)]);
    setSaving(true);
    setError(null);
    const results = await Promise.allSettled(jobs.map(([, run]) => run()));
    setSaving(false);
    refresh();
    const failed = results.map((r, i) => (r.status === 'rejected' ? `${jobs[i][0]}: ${errorMessage(r.reason)}` : null)).filter(Boolean);
    if (failed.length) {
      // Whatever did save is now the saved value; keep only the failed edits pending.
      results.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        const name = jobs[i][0];
        if (name === 'Notifications') setNotify(null);
        if (name === 'Polls and surveys') setPoll(null);
        if (name === 'Permissions') setPermDraft(null);
      });
      setError(`Not saved. ${failed.join(' ')}`);
      return;
    }
    toast('Channel settings saved', 'success');
    onClose();
  };

  // t722u: private or open. Asks, then saves at once (the rest of the dialog's edits still wait for Save).
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const followsProject = conv.privacy_project ?? null;
  const privacyLocked = !canManage || Boolean(followsProject) || privacyBusy || !isChannel;
  const setPrivate = async (v: boolean) => {
    if (privacyLocked || v === conv.is_private) return;
    if (!(await ask({ title: channelPrivacyTitle(v, conv.slug), body: channelPrivacyEffect(v), confirmLabel: 'Save', danger: false }))) return;
    setPrivacyBusy(true);
    try {
      await api.updateConversation(conv.id, { is_private: v });
      refresh();
      for (const key of [['browse', ws ?? ''], ['conversation-readers', conv.id], ['channel-access', conv.id]]) void queryClient.invalidateQueries({ queryKey: key });
      toast(v ? 'Channel is now private' : 'Channel is now open', 'success');
    } catch (e) { toast(errorMessage(e), 'error'); }
    finally { setPrivacyBusy(false); }
  };

  const archive = () => {
    void (conv.archived_at ? Promise.resolve(true) : ask({ title: `Archive #${conv.slug}?`, body: 'Nobody will be able to post in it. Type the channel name to confirm.', confirmLabel: 'Archive channel', typeToConfirm: conv.slug ?? '' }))
      .then(async ok => {
        if (!ok) return;
        try {
          await api.updateConversation(conv.id, { archived: !conv.archived_at });
          refresh();
          toast(conv.archived_at ? 'Channel unarchived' : 'Channel archived', 'success');
          onClose();
        } catch (e) { toast(errorMessage(e), 'error'); }
      });
  };

  const remove = () => {
    const name = conv.slug ?? conv.title ?? '';
    void ask({ title: `Delete #${name}?`, body: 'This deletes the channel for everyone and can’t be undone. Type the channel name to confirm.', confirmLabel: 'Delete channel', typeToConfirm: name })
      .then(async ok => {
        if (!ok) return;
        try {
          await api.deleteChannel(conv.id, name);
          queryClient.removeQueries({ queryKey: qk.conversation(conv.id) });
          void queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
          toast('Channel deleted', 'success');
          onClose();
          nav('/chat', { replace: true });
        } catch (e) {
          toast(errorMessage(e), 'error');
          void deletion.refetch();
        }
      });
  };

  return (
    <Modal open wide="lg" onClose={onClose} title={`#${conv.slug ?? conv.title} settings`}
      footer={<DialogFooter onCancel={onClose} onSave={() => void saveAll()} saving={saving} disabled={!dirty || invalid} error={error} saveTestId="channel-settings-save" />}>
      <Tabs value={tab} onChange={setTab} className="-mt-2 mb-4" tabs={[
        { id: 'about', label: 'About' }, { id: 'access', label: 'Access' }, { id: 'notifications', label: 'Notifications' }, { id: 'posting', label: 'Posting' },
        { id: 'translation', label: 'Translation' }, { id: 'colour', label: 'Sidebar colour' }, { id: 'members', label: 'Members' }, { id: 'danger', label: owner ? 'Leave, archive & delete' : 'Leave & archive' },
      ]} />
      {ro && tab !== 'notifications' && tab !== 'members' && tab !== 'colour' && tab !== 'access' && tab !== 'danger' && <p className="mb-3 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">Only channel owners and admins can change these. Ask one if something needs updating.</p>}

      {tab === 'about' && (
        <div className="space-y-4">
          <Field label="Name" hint="Lower case, no spaces; shown as #name"><Input disabled={ro} value={about.title} onChange={e => setAbout({ ...about, title: e.target.value })} /></Field>
          <Field label="Description" hint="What the channel is for; shown at the top of the channel"><Textarea disabled={ro} rows={3} value={about.description} onChange={e => setAbout({ ...about, description: e.target.value })} /></Field>
          <Field label="Topic" hint="Short line in the channel header"><Input disabled={ro} value={about.topic} onChange={e => setAbout({ ...about, topic: e.target.value })} /></Field>
          {conv.kind === 'channel' && <Field label="Building"><BuildingSelect disabled={ro} value={building} onChange={setBuilding} /></Field>}{/* t621u: never on DMs */}
        </div>
      )}

      {tab === 'access' && <div className="space-y-6" data-testid="channel-access-tab">
        {isChannel && (
          <div data-testid="channel-private">
            <Toggle checked={Boolean(conv.is_private)} onChange={v => void setPrivate(v)} disabled={privacyLocked} testId="channel-private-toggle"
              label={<span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" />{CHANNEL_PRIVATE_LABEL}</span>} />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="channel-private-note">
              {followsProject ? channelPrivacyProjectNote(followsProject.name) : conv.is_private ? 'Only members can see it' : 'Anyone in the workspace can find and join it'}
            </p>
          </div>
        )}
        {accessDraft ? <ChannelAccessPicker value={accessDraft} onChange={setAccessDraft} disabled={!canManage} /> : null}
        {/* t245u: role grants for this channel moved here from the Members window. Adding or removing one happens at once. */}
        {canGrant && <GrantsPanel mode="scope" scope={`conversation:${conv.id}`} title="Extra roles in this channel" />}
      </div>}

      {tab === 'colour' && <SidebarColourSettings type="conversation" id={conv.id} draft={colourDraft} onDraft={setColourDraft} />}

      {tab === 'notifications' && (
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm text-slate-600 dark:text-slate-300">Notify me about</legend>
          {([['all', 'All new messages'], ['mentions', 'Only @mentions and replies to me'], ['none', 'Nothing (muted)']] as const).map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm"><input type="radio" name="notify" checked={(notify ?? conv.notify_level) === v} onChange={() => setNotify(v)} />{l}</label>
          ))}
          <p className="pt-2 text-xs text-slate-500">This only changes your notifications. Quiet hours and email digests are in Settings → Notifications.</p>
        </fieldset>
      )}

      {tab === 'posting' && (
        <div className="space-y-5">
          <Field label="Who can start polls and surveys">
            <Select disabled={!moderator} value={poll ?? conv.poll_policy ?? 'members'} onChange={e => setPoll(e.target.value as PollPolicy)}>
              <option value="members">Everyone in the channel</option><option value="moderators">Only channel admins</option>
            </Select>
          </Field>
          <Field label="Who can forward messages out of this channel">
            <Select disabled={ro} value={s.forwarding ?? 'everyone'} onChange={e => setS({ forwarding: e.target.value as ForwardingPolicy })} data-testid="channel-forwarding">
              <option value="everyone">Everyone in the channel</option><option value="managers">Only channel managers</option><option value="nobody">Nobody</option>
            </Select>
          </Field>
          <Field label="Acknowledgement requests: default due time" hint="Pre-fills the due time when someone asks for acknowledgement here">
            <Select disabled={ro} value={s.ack_due_hours ?? ''} onChange={e => setS({ ack_due_hours: e.target.value ? Number(e.target.value) : null })}>
              <option value="">No due time</option><option value={4}>4 hours after posting</option><option value={24}>1 day</option><option value={48}>2 days</option><option value={72}>3 days</option><option value={168}>1 week</option>
            </Select>
          </Field>
          <div>
            <Toggle checked={Boolean(s.threads_collapsed)} onChange={v => !ro && setS({ threads_collapsed: v })} label="Start threads collapsed" />
            <p className="mt-1 text-xs text-slate-500">For people who haven’t chosen a thread view in this channel themselves. Busy channels read better collapsed.</p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <div><div className="text-sm font-medium">Channel form</div><div className="text-xs text-slate-500">{conv.form_template_id ? 'New posts use a form' : 'Posts are free text'}</div></div>
            {canAssignForm && <Button size="sm" variant="secondary" onClick={onOpenForm}>Change…</Button>}
          </div>
          {formFields && journeyLink.info && (journeyLink.info.can_manage || journeyLink.info.link?.enabled) && <FormJourneyLinkFields state={journeyLink} fields={formFields} />}
          {isChannel && <ChannelPermissionsSection convId={conv.id} saved={perms.data?.rules} draft={permDraft} onDraft={setPermDraft} disabled={!canManage} />}
        </div>
      )}

      {tab === 'translation' && (
        <div className="space-y-3">
          <Toggle checked={Boolean(s.auto_translate)} onChange={v => !ro && setS({ auto_translate: v })} label="Translate posts automatically for people who read in another language" />
          <p className="text-xs text-slate-500">
            Readers who have chosen a language in Settings see posts here translated, with the original one click away. People who turned off auto-translate still see the original.
            {!ai.data?.translation.provider && ' Translation is not switched on for this workspace yet; this takes effect once it is.'}
          </p>
        </div>
      )}

      {tab === 'members' && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm">{conv.member_count ?? conv.members?.length ?? 0} members{conv.is_private ? ' · private channel' : ''}</p>
          <Button variant="secondary" onClick={onOpenMembers}>Manage members</Button>
        </div>
      )}

      {tab === 'danger' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <div><div className="text-sm font-medium">Leave channel</div><div className="text-xs text-slate-500">You can rejoin a public channel at any time.</div></div>
            <Button variant="secondary" onClick={onLeave}><LogOut className="h-4 w-4" />Leave</Button>
          </div>
          {canManage && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 p-3 dark:border-rose-900">
              <div><div className="text-sm font-medium">{conv.archived_at ? 'Unarchive channel' : 'Archive channel'}</div><div className="text-xs text-slate-500">{conv.archived_at ? 'People can post here again.' : 'Nobody can post; history stays searchable.'}</div></div>
              <Button variant={conv.archived_at ? 'secondary' : 'danger'} onClick={archive}>
                <Archive className="h-4 w-4" />{conv.archived_at ? 'Unarchive' : 'Archive'}
              </Button>
            </div>
          )}
          {owner && deletion.data && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 p-3 dark:border-rose-900" data-testid="channel-delete">
              <div>
                <div className="text-sm font-medium">Delete channel</div>
                <div className="text-xs text-slate-500">{deletion.data.can_delete ? 'Removes the channel for everyone. This can’t be undone.' : deletion.data.message}</div>
              </div>
              <Button variant="danger" onClick={remove} disabled={!deletion.data.can_delete} data-testid="channel-delete-button">
                <Trash2 className="h-4 w-4" />Delete
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
